import "./propertyComparer.js"; // don't remove this import, it registers the comparers
import "./converter.js"; // don't remove this import, it registers the converters

import { IUniform, Material, Object3D, Texture, Uniform, WebGLRenderer } from "three";
import { isProxyInvariantError } from "../../../base/common/errors.js";
import { ILogService } from "../../log/common/log.js";
import { IProperty, IPropertyCollection, IPropertyProvider, PropertyUtils, Property, PropertyAddress, IPropertyGroup, PropertyCollection, PropertyGroup, IPropertyService, IPropertyStateProvider, GetPropertyOptions, PropertyViewMode, PropertyScope } from "../../properties/common/properties.js";
import { isCamera, isLikelyMaterialTextureInputSlot, isMaterial, isMeshLike, isObject3D, isPerspectiveCamera, isRenderer, isScene } from "./helper.js";
import { isColorLike, isTextureLike, isVectorPropertyLike } from "../../properties/common/types.js";
import { ThreeJs } from "./webgl.js";
import { IFileService } from "../../files/common/files.js";
import { getSourceCode } from "../../../base/common/coding.js";
import { Action } from "../../../base/common/actions.js";
import { IPropertyAccessorService } from "../../properties/common/accessor.js";
import { ColorPropertyAccessor, TexturePropertyAccessor, ThreeObjectMetadataAccessor, VectorPropertyAccessor, TSLInputNodeAccessor } from "./accessor.js";
import { getMetadata, postprocessThreeProperty } from "./propertyProvider.metadata.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";
import { PropertyStateProviderGroup, ViewModePropertyStateProvider } from "../../properties/common/propertyService.js";
import { ThreeDefaultViewMode } from "./propertyProvider.viewmode.js";
import { CommandService } from "../../commands/common/commandService.js";
import { Attachment } from "../../../base/common/attachment.js";
import { NeedleMeta } from "../../../base/common/needle.js";
import { ContextKeyExpr, ContextKeyFalseExpr } from "../../contextkey/common/contextkey.js";
import { ignoreNodeMaterialProperty, isNodeLike, isNodeMaterial, isVarNode } from "./helper.nodes.js";
import { extractTSLNodeProperties, createTSLNodeProperty } from "./propertyProvider.tsl.js";


export class ThreePropertyProvider implements IPropertyProvider {

    constructor(
        private readonly engine: ThreeJs,
        @ILogService private readonly logger: ILogService,
        @IFileService private readonly fileService: IFileService,
        @IPropertyService private readonly propertyService: IPropertyService,
        @IPropertyAccessorService private readonly propertyAccessorService: IPropertyAccessorService,
        @IStorageService private readonly storageService: IStorageService,

    ) {
        propertyAccessorService.registerAccessor(ThreeObjectMetadataAccessor);
        propertyAccessorService.registerAccessor(ColorPropertyAccessor);
        propertyAccessorService.registerAccessor(VectorPropertyAccessor);
        propertyAccessorService.registerAccessor(TexturePropertyAccessor);
        propertyAccessorService.registerAccessor(TSLInputNodeAccessor);
    }

    canHandle(object: object): boolean {
        return isObject3D(object) || isRenderer(object);
    }


    private readonly object3DIgnore = [
        "needle_isActiveInHierarchy",
        "matrixWorldNeedsUpdate",
    ]

    async getProperties(object: Object3D | WebGLRenderer, options: GetPropertyOptions): Promise<IPropertyCollection | null> {

        let name = "Object";
        let type = "Object";
        if (isObject3D(object)) {
            type = object.type;
            name = object.name || object.type;
        }
        else if (isRenderer(object)) {
            type = "Renderer";
            name = "type" in object && typeof object.type === "string" ? object.type : "WebGLRenderer";
        }

        const collection = new PropertyCollection(name, []);

        const state = new PropertyStateProviderGroup(
            options.viewMode === PropertyViewMode.DEFAULT ? new ThreeDefaultViewMode() : null,
            new ViewModePropertyStateProvider(options.viewMode, this.storageService)
        );

        const group = new PropertyGroup(type, "Object", [], [
            new Action("log-object", "Print Object", true, async () => {
                console.log(`Printing object: ${name}`, object);
            }),
            new Action("get-sourcecode", "Get Code", true, async () => {
                return getSourceCode(object);
            }),
        ]);
        group.addCategory("three")
        collection.groups.push(group);


        // #region Renderer Properties
        if (isRenderer(object)) {
            group.name = "Renderer";

            let docsUrl: string | undefined = undefined;
            if (object.isWebGLRenderer) {
                docsUrl = `https://threejs.org/docs/#WebGLRenderer`;
            }
            else {
                docsUrl = `https://threejs.org/docs/#WebGPURenderer`;
            }
            if (docsUrl) {
                group.attachments.push(new Attachment("open-docs-" + group.name, "", "Open documentation for " + group.name, "icon-docs",
                    async () => {
                        CommandService.get().executeCommand("editor.action.url.openUrl", docsUrl);
                    }));
            }

            const props = PropertyUtils.getAllObjectProperties(object);
            for (const key in props) {
                if (state.isHidden(object, key)) {
                    continue;
                }
                const property = this.createProperty(object, key);
                if (property) {
                    group.properties.push(property);
                }
            }
            // const prop = new class extends Property {
            //     constructor() {
            //         super(object, "renderTarget", "texture", false, true, false, {
            //             description: "The current render target. If null, the default framebuffer is used.",
            //         },);
            //         this.label = "renderTarget";
            //     }
            //     get value() {
            //         return object.getRenderTarget();
            //     }
            // }();
            // group.properties.push(prop);
        }
        // #region Object3D
        else if (isObject3D(object)) {
            if (object.type && typeof object.type === "string" && object.type.length > 0) {
                group.name = object.type;
            }

            const docsUrl = `https://threejs.org/docs/#${group.name}`;
            group.attachments.push(new Attachment("open-docs-" + group.name, "", "Open documentation for " + group.name, "icon-docs",
                async () => {
                    CommandService.get().executeCommand("editor.action.url.openUrl", docsUrl);
                }));

            const props = PropertyUtils.getAllObjectProperties(object);


            for (const _key in props) {

                if (this.object3DIgnore.includes(_key)) continue;

                if (isScene(object) && _key === "autoUpdate") {
                    // autoUpdate was renamed to matrixWorldAutoUpdate in r144. Skip to avoid three warnings flood e.g. in https://next.junni.co.jp/
                    continue;
                }

                switch (_key) {
                    case "geometry___needle":
                    case "material___needle":
                        continue;
                }

                let propertyOwner = object as object;
                let propertyKey = _key;
                if (_key === "layers") {
                    if (isObject3D(object)) {
                        propertyOwner = object.layers;
                        propertyKey = "mask";
                    }
                }

                if (state.isHidden(object, propertyKey)) {
                    continue;
                }

                const property = this.createProperty(propertyOwner, propertyKey);
                if (property) {
                    group.properties.push(property);

                    if (isPerspectiveCamera(propertyOwner)) {
                        switch (propertyKey) {
                            case "fov":
                            case "near":
                            case "far":
                            case "aspect":
                            case "zoom":
                            case "focus":
                            case "filmGauge":
                            case "filmOffset":
                                property.onDidChange(() => {
                                    propertyOwner.updateProjectionMatrix();
                                });
                                break;
                        }
                    }
                    // TODO: show background and environment texture properties!
                    else if (isScene(propertyOwner)) {
                        switch (propertyKey) {
                            case "background":
                            case "environment":
                                Internal.addTextureProperties(object, object, propertyKey, property, propertyKey, group);
                                break;
                        }
                    }
                }
            }

            // #region Material
            const materials = Internal.collectMaterialProperties(object, this, this.propertyAccessorService, state, this.logger);
            if (materials) {
                for (const matprops of materials) {
                    if (matprops) collection.groups.push(matprops);
                }
            }

            // #region Needle Components
            if (object.userData?.components) {
                const components = object.userData.components as object[];
                if (Array.isArray(components)) {
                    for (const comp of components) {
                        const needleComponent = comp as Needle.MaybeNeedleRuntimeComponent;
                        const componentName = needleComponent.__name || comp.constructor.name;
                        const componentsGroup = new PropertyGroup(componentName, componentName, [],
                            [
                                new Action("log-object", "Print Object", true, async () => {
                                    console.log(`Printing object: ${name}`, comp);
                                }),
                                new Action("get-sourcecode", "Get Code", true, async () => {
                                    return getSourceCode(comp);
                                }),
                            ]
                        );
                        componentsGroup.addCategory("components");
                        componentsGroup.icon = "component";
                        collection.groups.push(componentsGroup);

                        const enabledProperty = new Property(comp, "enabled", "boolean", {});
                        componentsGroup.properties.push(enabledProperty);

                        Needle.collectComponentProperties("runtime", needleComponent, this, componentsGroup, state, this.logger);
                    }
                }
            }
            // glTF built-in components
            else if (object.userData.gltfExtensions?.NEEDLE_components) {
                const components = object.userData.gltfExtensions.NEEDLE_components.builtin_components;
                this.logger.debug("Found builtin_components in userData:", components);
                for (const comp of components) {
                    const needleComponent = comp as Needle.NeedleGltfComponent;
                    const componentName = needleComponent.name;
                    const componentsGroup = new PropertyGroup(componentName, `${componentName} (glTF)`, [],
                        [
                            new Action("log-object", "Print Object", true, async () => {
                                console.log(`Printing object: ${name}`, comp);
                            }),
                            new Action("get-sourcecode", "Get Code", true, async () => {
                                return getSourceCode(comp);
                            }),
                        ]
                    );
                    componentsGroup.addCategory("components");
                    componentsGroup.icon = "component";
                    collection.groups.push(componentsGroup);

                    const enabledProperty = new Property(comp, "enabled", "boolean", {});
                    componentsGroup.properties.push(enabledProperty);

                    Needle.collectComponentProperties("gltf", needleComponent, this, componentsGroup, state, this.logger);
                }
            }

        }
        // #endregion Object3D




        return collection;
    }



    createProperty(object: Record<string, unknown>, key: string): IProperty | null {

        let label: string | undefined = undefined;

        const desc = PropertyUtils.findPropertyDescriptor(object, key);
        if (!desc) {
            this.logger.warn(`ThreePropertyProvider: No property descriptor found for key ${key} on object`);
            return null;
        }

        if (isMaterial(object) && key.endsWith("___needle")) {
            // the ___needle properties are internal metadata, skip them
            return null;
        }

        // Try to read the value, but catch Proxy invariant violations
        // This can happen when the object is wrapped in a proxy (e.g., Threlte/Svelte reactivity)
        // and the property is non-configurable with an object value.
        // Also catches getter errors (e.g., third-party toJSON / "Method not implemented.")
        let value: unknown;
        try {
            value = desc.value || (desc.get ? desc.get.call(object) : (object as any)[key]);
        }
        catch (err) {
            if (isProxyInvariantError(err)) {
                this.logger.debug(`ThreePropertyProvider: Skipping property '${key}' due to proxy invariant violation`);
            } else {
                this.logger.warn(`ThreePropertyProvider: Skipping property '${key}' due to error:`, err);
            }
            return null;
        }
        let type = typeof value;

        if (type === "function") {
            return null; // Skip functions
        }


        if (Needle.isNeedleVolumeParameter(value)) {
            object = object[key] as any;
            label = key;
            key = "value";
            value = (object as any)[key];
            type = typeof value;
        }
        // For @serializable properties undefined types are primitives
        else if (Needle.isNeedleRuntimeComponent(object)) {
            if (typeof type === "undefined" || type === null) {
                const valueType = typeof value;
                if (valueType === "string" || valueType === "number" || valueType === "boolean") {
                    type = valueType;
                }
            }
            if (!type) {
                return null; // cannot determine type
            }
        }

        let editable = true;
        if (desc.get && desc.set === undefined) {
            editable = false; // Read-only if no setter
        }
        else if (value instanceof Array) {
            editable = false;
        }

        let hidden = false;
        if (key.startsWith("_")) {
            hidden = true;
        }

        // For materials
        if (key === "needsUpdate" && !desc.get) {
            type = "boolean";
        }
        if (isNodeLike(value)) {
            if (value.nodeType)
                type = value.nodeType as any;
            if (isVarNode(value)) {
                type = value.node.nodeType as any;
            }
        }

        const readable = desc.get !== undefined || desc.value !== undefined;
        const writeable = desc.set !== undefined || desc.writable || false;
        const meta = getMetadata(object, key, value);
        meta.editable = editable;
        meta.writeable = writeable;
        meta.readable = readable;
        const property = new Property(object, key, type, meta, this.propertyAccessorService);
        property.hidden = hidden;
        if (label !== undefined) property.label = label;

        if (isMaterial(object)) {
            // When a material property changes, we need to set needsUpdate to true
            property.onDidChange((e) => {
                // When the version changes we don't need to set needsUpdate, as it is done automatically
                if (key === "version" || key === "needsUpdate" || key === "uniformsNeedUpdate") {
                    return;
                }
                // Only trigger needsUpdate when the actual value changed, not for metadata changes (e.g., hidden)
                if (e.value !== true) {
                    return;
                }
                const mat = object as Material;
                mat.needsUpdate = true;
            });
        }

        postprocessThreeProperty(property);
        return property;
    }

    async findProperty(address: PropertyAddress): Promise<IProperty | null> {
        const obj = address.objectId;

        const object = this.engine.findObjectById ? this.engine.findObjectById(obj) : null
        if (!object) {
            this.logger.warn(`ThreePropertyProvider: No object found with ID ${obj}`);
            return null;
        }

        this.logger.debug(`ThreePropertyProvider: Finding property ${address.propertyPath} on object with ID ${obj}`);

        let currentObject: object = object as object;
        const segments = address.propertyPath.split('.');
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            if (!currentObject) {
                throw new Error(`ThreePropertyProvider: Cannot resolve property path ${address.propertyPath} on object with ID ${obj}. Segment ${segment} is undefined.`);
            }

            const isLastSegment = (i === segments.length - 1);
            if (isLastSegment) {
                const prop = this.createProperty(currentObject, segment);
                return prop;
            }

            const isArrayIndex = segment.endsWith(']') && segment.includes('[');
            if (isArrayIndex) {
                const index = Number(segment.substring(segment.indexOf('[') + 1, segment.length - 1));
                currentObject = (currentObject as any)[index];
                continue;
            }
            currentObject = (currentObject as any)[segment];
        }

        this.logger.warn(`ThreePropertyProvider: Property ${address.propertyPath} not found on object with ID ${obj}`);
        return null;
    }




}




namespace Internal {


    // #region Material Properties
    export function collectMaterialProperties(object: Object3D, prov: ThreePropertyProvider, accessor: IPropertyAccessorService, state: IPropertyStateProvider, logger: ILogService): null | Array<IPropertyGroup | null> {

        if (isMeshLike(object)) {
            const mat = object.material;

            if (Array.isArray(mat)) {
                const groups: IPropertyGroup[] = [];
                for (let i = 0; i < mat.length; i++) {
                    const material = mat[i];
                    const group = getMaterialProperties(object, material, prov, accessor, state, logger);
                    if (group) {
                        groups.push(group);
                    }
                }
                return groups;
            }
            else {
                const group = getMaterialProperties(object, mat, prov, accessor, state, logger);
                if (group) {
                    return [group];
                }
            }
        }

        return null;
    }

    function getMaterialProperties(object: Object3D, material: Material, prov: ThreePropertyProvider, accessor: IPropertyAccessorService, state: IPropertyStateProvider, logger: ILogService): IPropertyGroup | null {

        const keys = PropertyUtils.getAllObjectProperties(material);
        const group = new PropertyGroup("material", material.type || "Material", [],
            [
                new Action("log-object", "Print Object", true, async () => {
                    console.log(`Printing object: ${material.name} (${material.type})`, material);
                }),
                new Action("get-sourcecode", "Get Code", true, async () => {
                    return getSourceCode(material);
                }),
                new Action("get-sourcecode", "Get Vertex Shader", "vertexShader" in material, async () => {
                    return (material as any).vertexShader;
                }),
                new Action("get-sourcecode", "Get Fragment Shader", "fragmentShader" in material, async () => {
                    return (material as any).fragmentShader;
                }),
            ]
        );
        group.addCategory("materials");

        let materialUrl = `https://threejs.org/docs/#api/en/materials/${material.type}`;
        if ((material as any).isMeshStandardMaterial && material.type === "StandardMaterial") { // old material type https://www.kodeclubs.com/
            materialUrl = `https://threejs.org/docs/#api/en/materials/MeshStandardMaterial`;
        }
        group.attachments.push(new Attachment("open-docs-" + group.name, "", "Open documentation for " + group.name, "icon-docs",
            async () => {
                const docsUrl = materialUrl;
                CommandService.get().executeCommand("editor.action.url.openUrl", docsUrl);
            })
        );


        group.icon = "material";
        const isNodeMat = isNodeMaterial(material);

        for (const key in keys) {

            if (state.isHidden(material, key)) {
                continue;
            }

            // Check if this property should be ignored (works for both NodeMaterials and regular materials with TSL nodes)
            if (isNodeMat && ignoreNodeMaterialProperty(material, key)) {
                continue;
            }

            // Also check for TSL node properties on non-NodeMaterials (e.g., MeshPhysicalMaterial with WebGPU)
            if (!isNodeMat && key.endsWith("Node")) {
                const value = (material as any)[key];
                if (isNodeLike(value)) {
                    continue;
                }
            }

            const desc = keys[key];
            const value = desc.value || (desc.get ? desc.get.call(material) : material[key]);
            let type = desc.get ? typeof desc.get.call(material) : typeof value;

            if (type === "function") continue; // Skip functions

            let editable = true;
            // const readable = desc.get !== undefined || desc.value !== undefined;
            // let writeable = desc.set !== undefined || desc.writable;
            if (desc.get && desc.set === undefined) {
                editable = false; // Read-only if no setter
            }
            else if (value instanceof Array) {
                editable = false;
            }

            if (key === "needsUpdate" && !desc.get) {
                type = "boolean";
            }

            const property = prov.createProperty(material, key);
            if (property) {
                property.metadata.address = object.id + ".material." + key;
                group.properties.push(property);

                addTextureProperties(object, material, key, property, key, group);

                if (key === "uniforms") {
                    if (value && typeof value === "object") {
                        for (const uniformName in value) {
                            const uniform = value[uniformName] as IUniform;
                            const uniformValue = uniform?.value || null;
                            const metadata = getMetadata(uniform, "value", uniformValue);
                            if (isLikelyMaterialTextureInputSlot(uniformName, uniformValue)) {
                                metadata.accept = "null, image/*";
                            }
                            metadata.contextKey = `material.uniforms.${uniformName}`;
                            const uniformProperty = new Property(uniform, "value", typeof uniformValue, metadata)
                            uniformProperty.label = uniformName;
                            postprocessThreeProperty(uniformProperty);
                            group.properties.push(uniformProperty);

                            addTextureProperties(object, uniform, "value", uniformProperty, uniformName, group);

                            uniformProperty.onDidChange(() => {
                                logger.debug("Uniform changed:", uniformName, uniform.value, material.name);
                                material.needsUpdate = true;
                                if ("uniformsNeedUpdate" in material) {
                                    (material as any).uniformsNeedUpdate = true;
                                }
                                if ("needsUpdate" in uniform) {
                                    uniform.needsUpdate = true;
                                }
                            });
                        }
                    }
                }
            }
        }

        // Add TSL node properties for all materials (both NodeMaterials and regular materials with WebGPU TSL nodes)
        // Check if the material has any TSL node properties
        const tslProperties = extractTSLNodeProperties(material, material.type);
        if (tslProperties.length > 0) {

            // Group TSL properties by category for better organization
            const categorizedProps = new Map<string, typeof tslProperties>();
            for (const propInfo of tslProperties) {
                const category = propInfo.config.category;
                if (!categorizedProps.has(category)) {
                    categorizedProps.set(category, []);
                }
                categorizedProps.get(category)!.push(propInfo);
            }

            // Add TSL properties to the group
            for (const propInfo of tslProperties) {
                if (state.isHidden(material, propInfo.key)) {
                    continue;
                }

                const tslProperty = createTSLNodeProperty(material, propInfo, object.id, accessor);
                group.properties.push(tslProperty);
            }
        }

        if (group.properties.length === 0) {
            return null;
        }
        return group;
    }

    const threeTextureFormats = [
        { label: "AlphaFormat", value: 1021 },
        { label: "RGBFormat", value: 1022 },
        { label: "RGBAFormat", value: 1023 },
        { label: "DepthFormat", value: 1026 },
        { label: "DepthStencilFormat", value: 1027 },
        { label: "RedFormat", value: 1028 },
        { label: "RedIntegerFormat", value: 1029 },
        { label: "RGFormat", value: 1030 },
        { label: "RGIntegerFormat", value: 1031 },
        { label: "RGBIntegerFormat", value: 1032 },
        { label: "RGBAIntegerFormat", value: 1033 },
        { label: "RGB_S3TC_DXT1_Format", value: 33776 },
        { label: "RGBA_S3TC_DXT1_Format", value: 33777 },
        { label: "RGBA_S3TC_DXT3_Format", value: 33778 },
        { label: "RGBA_S3TC_DXT5_Format", value: 33779 },
        { label: "RGB_PVRTC_4BPPV1_Format", value: 35840 },
        { label: "RGB_PVRTC_2BPPV1_Format", value: 35841 },
        { label: "RGBA_PVRTC_4BPPV1_Format", value: 35842 },
        { label: "RGBA_PVRTC_2BPPV1_Format", value: 35843 },
        { label: "RGB_ETC1_Format", value: 36196 },
        { label: "RGB_ETC2_Format", value: 37492 },
        { label: "RGBA_ETC2_EAC_Format", value: 37496 },
        { label: "R11_EAC_Format", value: 37488 },
        { label: "SIGNED_R11_EAC_Format", value: 37489 },
        { label: "RG11_EAC_Format", value: 37490 },
        { label: "SIGNED_RG11_EAC_Format", value: 37491 },
        { label: "RGBA_ASTC_4x4_Format", value: 37808 },
        { label: "RGBA_ASTC_5x4_Format", value: 37809 },
        { label: "RGBA_ASTC_5x5_Format", value: 37810 },
        { label: "RGBA_ASTC_6x5_Format", value: 37811 },
        { label: "RGBA_ASTC_6x6_Format", value: 37812 },
        { label: "RGBA_ASTC_8x5_Format", value: 37813 },
        { label: "RGBA_ASTC_8x6_Format", value: 37814 },
        { label: "RGBA_ASTC_8x8_Format", value: 37815 },
        { label: "RGBA_ASTC_10x5_Format", value: 37816 },
        { label: "RGBA_ASTC_10x6_Format", value: 37817 },
        { label: "RGBA_ASTC_10x8_Format", value: 37818 },
        { label: "RGBA_ASTC_10x10_Format", value: 37819 },
        { label: "RGBA_ASTC_12x10_Format", value: 37820 },
        { label: "RGBA_ASTC_12x12_Format", value: 37821 },
        { label: "RGBA_BPTC_Format", value: 36492 },
        { label: "RGB_BPTC_SIGNED_Format", value: 36494 },
        { label: "RGB_BPTC_UNSIGNED_Format", value: 36495 },
        { label: "RED_RGTC1_Format", value: 36283 },
        { label: "SIGNED_RED_RGTC1_Format", value: 36284 },
        { label: "RED_GREEN_RGTC2_Format", value: 36285 },
        { label: "SIGNED_RED_GREEN_RGTC2_Format", value: 36286 },
    ];
    const formatKeysWithoutAlphaChannel = [
        1022, 1026, 1028, 33776, 35840, 36196, 37492, 36494, 36283, 36284,
    ];
    const formatKeysWithAlphaChannel = threeTextureFormats.map(f => f.value).filter(v => !formatKeysWithoutAlphaChannel.includes(v));


    /**
     * Adds texture related sub-properties for a texture property (like format, width, height, colorSpace, mapping, channel, etc.)
     * @param object The Object3D the material belongs to (it might be a scene)
     * @param propertyOwner The material or object that owns the texture property (can be the same as object)
     * @param key The key of the texture property
     * @param property The texture property
     * @param propertyName The name of the texture property (for uniforms it's the uniform name)
     * @param group The property group to add the sub-properties to
     */
    export function addTextureProperties(object: Object3D, propertyOwner: Material | Object3D | IUniform, key: string, property: IProperty, propertyName: string, group: IPropertyGroup) {

        if (!property.metadata.contextKey) {
            return;
        }

        const value = (propertyOwner as any)[key];

        // HACK: until we have a way to update the ContextKeyService dynamically, we skip color properties
        // maybe we can just pass in the contextkeyservice here?
        if (isColorLike(value)) {
            return;
        }

        const mayHaveTextureOrIsTexture = isTextureLike(value) || property.metadata.accept?.includes("image/*");

        if (!mayHaveTextureOrIsTexture) return;

        // TODO: we might need to expose this for uniforms
        function updateTexture() {
            const texture = (propertyOwner as any)[key] as Texture;
            if (isTextureLike(texture) || (typeof texture === "object" && texture !== null && "version" in texture)) {
                texture.version += 1;
            }
        }

        const groupDetailsId: string | undefined = `details.${object.id}.material.${propertyName}`;
        const groupDetailsLabel: string | undefined = `${propertyName} details`;

        const OFFSET = 0.00001;
        let offsetCount = 0;

        // TODO: pass in registry to dynamically check if something is a color
        const when = ContextKeyExpr.notEquals(property.metadata.contextKey, null);

        // TODO: can we display texture type (e.g. DataTexture, etc.) as a string property?


        const formatContextKey = "material." + key + ".format";
        const formatProperty = new Property(propertyOwner, key + ".format", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.format`,
            inset: true,
            when: when,
            contextKey: formatContextKey,
            description: "The format of the texture.",
            writeable: false,
            editable: false,
            static: true,
            enumOptions: threeTextureFormats,
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        formatProperty.label = "format";
        postprocessThreeProperty(formatProperty);
        group.properties.push(formatProperty);


        const widthProperty = new Property(propertyOwner, key + ".image.width", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.image.width`,
            inset: true,
            when: when,
            description: "The width of the image.",
            writeable: false,
            editable: false,
            static: true,
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        widthProperty.label = "width";
        postprocessThreeProperty(widthProperty);
        group.properties.push(widthProperty);

        const heightProperty = new Property(propertyOwner, key + ".image.height", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.image.height`,
            inset: true,
            when: when,
            description: "The height of the image.",
            writeable: false,
            editable: false,
            static: true,
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        heightProperty.label = "height";
        postprocessThreeProperty(heightProperty);
        group.properties.push(heightProperty);

        const colorSpaceProperty = new Property(propertyOwner, key + ".colorSpace", "string", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.colorSpace`,
            inset: true,
            when: when,
            description: "The color space of the texture.",
            enumOptions: [
                { label: "NoColorSpace", value: "" },
                { label: "SRGBColorSpace", value: "srgb" },
                { label: "LinearSRGBColorSpace", value: "srgb-linear" },
            ],
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        colorSpaceProperty.onDidChange(() => {
            updateTexture();
        });
        colorSpaceProperty.label = "colorSpace";
        postprocessThreeProperty(colorSpaceProperty);
        group.properties.push(colorSpaceProperty);

        const uvChannelProperty = new Property(propertyOwner, key + ".channel", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.channel`,
            inset: true,
            when: when,
            description: "The UV channel used for the texture.",
            defaultValue: 0,
            enumOptions: [
                { label: "UV Channel 0", value: 0 },
                { label: "UV Channel 1", value: 1 },
                { label: "UV Channel 2", value: 2 },
                { label: "UV Channel 3", value: 3 },
            ],
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        // TODO: not sure if we need to update texture on channel change? let's just do it to be safe
        uvChannelProperty.onDidChange(() => {
            updateTexture();
        });
        uvChannelProperty.label = "channel";
        postprocessThreeProperty(uvChannelProperty);
        group.properties.push(uvChannelProperty);

        const mappingContextKey = "material." + key + ".mapping";
        const mappingProperty = new Property(propertyOwner, key + ".mapping", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.mapping`,
            inset: true,
            when: when,
            description: "The mapping mode of the texture.",
            contextKey: mappingContextKey,
            defaultValue: 300,
            enumOptions: [
                { label: "UVMapping", value: 300 },
                { label: "CubeReflectionMapping", value: 301 },
                { label: "CubeRefractionMapping", value: 302 },
                { label: "EquirectangularReflectionMapping", value: 303 },
                { label: "EquirectangularRefractionMapping", value: 304 },
                // { label: "SphericalReflectionMapping", value: 305 },
                { label: "CubeUVReflectionMapping", value: 306 },
                // { label: "CubeUVRefractionMapping", value: 307 }, // not in three.js?
            ],
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        mappingProperty.onDidChange(() => {
            updateTexture();
        });
        mappingProperty.label = "mapping";
        postprocessThreeProperty(mappingProperty);
        group.properties.push(mappingProperty);

        const flipyProperty = new Property(propertyOwner, key + ".flipY", "boolean", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.flipY`,
            inset: true,
            when: when,
            description: "Whether to flip the texture vertically.",
            defaultValue: false,
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        flipyProperty.onDidChange(() => {
            updateTexture();
        });
        flipyProperty.label = "flipY";
        postprocessThreeProperty(flipyProperty);
        group.properties.push(flipyProperty);

        const isNotEquirectMapping = ContextKeyExpr.and(
            ContextKeyExpr.notEquals(mappingContextKey, 303), // not EquirectangularReflectionMapping
            ContextKeyExpr.notEquals(mappingContextKey, 304), // not EquirectangularRefractionMapping
            ContextKeyExpr.notEquals(mappingContextKey, 306) // not CubeUVReflectionMapping
        );

        const repeatProperty = new Property(propertyOwner, key + ".repeat", "vector2", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.repeat`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
            defaultValue: { x: 1, y: 1 },
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        repeatProperty.label = "repeat";
        postprocessThreeProperty(repeatProperty);
        group.properties.push(repeatProperty);

        const offsetProperty = new Property(propertyOwner, key + ".offset", "vector2", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.offset`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
            defaultValue: { x: 0, y: 0 },
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        offsetProperty.label = "offset";
        postprocessThreeProperty(offsetProperty);
        group.properties.push(offsetProperty);

        const clampSProperty = new Property(propertyOwner, key + ".wrapS", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.wrapS`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
            description: "This defines how the texture is wrapped horizontally and corresponds to U in UV mapping.",
            defaultValue: 1001,
            enumOptions: [
                { label: "RepeatWrapping", value: 1000 },
                { label: "ClampToEdgeWrapping", value: 1001 },
                { label: "MirroredRepeatWrapping", value: 1002 },
            ],
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        clampSProperty.onDidChange(() => {
            updateTexture();
        });
        clampSProperty.label = "wrapS (U)";
        postprocessThreeProperty(clampSProperty);
        group.properties.push(clampSProperty);

        const clampTProperty = new Property(propertyOwner, key + ".wrapT", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.wrapT`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
            description: "This defines how the texture is wrapped horizontally and corresponds to V in UV mapping.",
            defaultValue: 1001,
            enumOptions: [
                { label: "RepeatWrapping", value: 1000 },
                { label: "ClampToEdgeWrapping", value: 1001 },
                { label: "MirroredRepeatWrapping", value: 1002 },
            ],
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        clampTProperty.onDidChange(() => {
            updateTexture();
        });
        clampTProperty.label = "wrapT (V)";
        postprocessThreeProperty(clampTProperty);
        group.properties.push(clampTProperty);


        const rotationProperty = new Property(propertyOwner, key + ".rotation", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.rotation`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
            description: "The rotation of the texture in radians.",
            defaultValue: 0,
            min: -Math.PI,
            max: Math.PI,
            step: 0.01,
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        rotationProperty.label = "rotation";
        postprocessThreeProperty(rotationProperty);
        group.properties.push(rotationProperty);

        // TODO: we can hide center if repeat is (1,1) and offset is (0,0) and rotation is 0? (not sure if that's useful though)
        const centerProperty = new Property(propertyOwner, key + ".center", "vector2", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.center`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
            defaultValue: { x: 0.5, y: 0.5 },
            description: "The center of rotation and scaling.",
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        centerProperty.label = "center";
        postprocessThreeProperty(centerProperty);
        group.properties.push(centerProperty);

        const premultiplyAlphaProperty = new Property(propertyOwner, key + ".premultiplyAlpha", "boolean", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.premultiplyAlpha`,
            inset: true,
            when: ContextKeyExpr.and(
                when,
                // TODO: we need to have a dynamic context key expression (or a way to trigger an context key update for the format when a texture is assigned - otherwise the context key expression is static and won't update when a texture is assigned from NULL to something (because initially f.value is null))
                ContextKeyExpr.or(
                    ...formatKeysWithAlphaChannel.map(fk => ContextKeyExpr.equals(formatContextKey, fk))
                )
            ),
            description: "Whether the alpha channel is premultiplied.",
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        premultiplyAlphaProperty.label = "premultiplyAlpha";
        postprocessThreeProperty(premultiplyAlphaProperty);
        group.properties.push(premultiplyAlphaProperty);



        const anistropyProperty = new Property(propertyOwner, key + ".anisotropy", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: `${object.id}.material.${key}.anisotropy`,
            inset: true,
            when: when,
            description: "The number of samples taken along the axis through the pixel that has the highest density of texels. By default, this value is 1. A higher value gives a less blurry result than a basic mipmap, at the cost of more texture samples being used.",
            min: 1,
            max: 7,
            step: 1,
            defaultValue: 1, // normally Texture.DEFAULT_ANISOTROPY and can be overriden but HERE we don't have access to the potentially external TEXTURE settings (e.g. in chrome extension context)
            detailsId: groupDetailsId,
            detailsLabel: groupDetailsLabel,
        });
        anistropyProperty.onDidChange(() => {
            updateTexture();
        });
        anistropyProperty.label = "anisotropy";
        postprocessThreeProperty(anistropyProperty);
        group.properties.push(anistropyProperty);
    }

}



// #region Needle

export namespace Needle {

    export function objectHasComponents(thing: unknown): { hasComponents: boolean; types: Array<"gltf" | "runtime"> } {
        const result = { hasComponents: false, types: [] as Array<"gltf" | "runtime"> };

        if (typeof thing === "object" && thing !== null) {
            const object = thing as Record<string, any>;
            if (object.userData?.components && Array.isArray(object.userData.components) && object.userData.components.length > 0) {
                result.hasComponents = true;
                result.types.push("runtime");
            }
            if (object.userData.gltfExtensions?.NEEDLE_components?.builtin_components?.length) {
                result.hasComponents = true;
                result.types.push("gltf");
            }
        }

        return result;
    }

    export function getNeedleComponents(thing: unknown): { runtimeComponents: Array<MaybeNeedleRuntimeComponent>; gltfComponents: Array<NeedleGltfComponent> } {
        const result = { runtimeComponents: [] as Array<MaybeNeedleRuntimeComponent>, gltfComponents: [] as Array<NeedleGltfComponent> };

        if (typeof thing === "object" && thing !== null) {
            const object = thing as Record<string, any>;
            if (object.userData?.components && Array.isArray(object.userData.components)) {
                for (const comp of object.userData.components) {
                    if (isNeedleRuntimeComponent(comp)) {
                        result.runtimeComponents.push(comp);
                    }
                }
            }
            if (object.userData?.gltfExtensions?.NEEDLE_components?.builtin_components && Array.isArray(object.userData.gltfExtensions.NEEDLE_components.builtin_components)) {
                for (const comp of object.userData.gltfExtensions.NEEDLE_components.builtin_components) {
                    if (isNeedleGltfComponent(comp)) {
                        result.gltfComponents.push(comp);
                    }
                }
            }
        }
        return result;
    }

    export type MaybeNeedleRuntimeComponent = Record<string | symbol, any> & {
        "__name"?: string;
        "$serializedTypes"?: Record<string, any>;
        "needle-component-name"?: string;
    }
    export function isNeedleRuntimeComponent(obj: any): obj is MaybeNeedleRuntimeComponent {
        return obj && typeof obj === "object" && obj.$serializedTypes && typeof obj.$serializedTypes === "object";
    }

    type VolumeParameter = { value: unknown };
    export function isNeedleVolumeParameter(obj: any): obj is VolumeParameter {
        return obj && typeof obj === "object" && "isVolumeParameter" in obj && obj.isVolumeParameter === true;
    }

    export type NeedleGltfComponent = {
        name: string,
        guid: string,
        enabled: boolean
    } & Record<string, any>;

    export function isNeedleGltfComponent(obj: any): obj is NeedleGltfComponent {
        return obj && typeof obj === "object" && typeof obj.name === "string" && typeof obj.guid === "string" && typeof obj.enabled === "boolean" && obj.gameObject === undefined; // to distinguish from runtime components which might also have name, guid and enabled properties
    }

    export function collectComponentProperties(type: "gltf" | "runtime", obj: MaybeNeedleRuntimeComponent | NeedleGltfComponent, prov: ThreePropertyProvider, group: IPropertyGroup, state: IPropertyStateProvider, logger: ILogService): void {

        const name = resolveNeedleComponentName(obj);
        if (name) {
            group.name = name;
            const docsUrl = `https://engine.needle.tools/docs/api/${group.name}`;
            group.attachments.push(new Attachment("open-docs-" + group.name, "", "Open documentation for " + group.name, "icon-docs",
                async () => {
                    CommandService.get().executeCommand("editor.action.url.openUrl", docsUrl);
                }));
            const typeInfo = NeedleMeta.getNeedleTypeMeta(group.name);
            group.description = NeedleMeta.getTypeDescriptionShort(group.name) || undefined;
            group.tooltip = typeInfo?.comment.text || typeInfo?.comment.shortText;
        }
        else {
            logger.warn("Needle Component: Could not resolve component name", obj);
        }

        if (isNeedleRuntimeComponent(obj)) {
            const types = obj.$serializedTypes;
            for (const key in types) {
                if (state.isHidden(obj, key)) {
                    continue;
                }
                const property = prov.createProperty(obj, key);
                if (property) {
                    postprocessComponentProperty(property);
                    group.properties.push(property);
                }
            }

        }
        else if (type === "gltf") {
            const keys = Object.keys(obj);
            for (const key of keys) {
                if (key === "name") continue; // skip name property for glTF components
                if (state.isHidden(obj, key)) {
                    continue;
                }
                // ignore symbols
                const property = prov.createProperty(obj, key);
                if (property) {
                    postprocessComponentProperty(property);
                    group.properties.push(property);
                }
            }
        }
    }

    const resolvedNameSymbols: Array<symbol> = [];

    export function resolveNeedleComponentName(obj: MaybeNeedleRuntimeComponent): string | null {
        for (const sym of resolvedNameSymbols) {
            const value = obj[sym];
            if (value) {
                return value;
            }
        }

        // walk up the prototype chain to find a more specific name
        let staticNAME: string | null = null;

        let proto = Object.getPrototypeOf(obj);
        while (proto) {
            // check if prototype has symbol named "needle-component-name"
            const symbols = Object.getOwnPropertySymbols(proto);
            for (const sym of symbols) {
                switch (sym.description) {
                    case "component-name":
                    case "original-component-name":
                        const value = obj[sym];
                        if (value && typeof value === "string") {
                            if (!resolvedNameSymbols.includes(sym))
                                resolvedNameSymbols.push(sym);
                            return value;
                        }
                        break;
                }
            }

            // check if a component has defined a static NAME property
            // e.g. { static NAME = "MyComponent" }
            // we use it as a fallback if no symbol is found
            const constructor = proto.constructor;
            if (!staticNAME && constructor?.["NAME"]) {
                staticNAME = constructor["NAME"];
                return staticNAME;
            }

            proto = Object.getPrototypeOf(proto);
        }

        // use the most specific static name if available
        if (staticNAME) {
            return staticNAME;
        }

        // Fallback
        if (obj["needle-component-name"]) {
            return obj["needle-component-name"];
        }

        // Fallback #2 (used in older engine versions like 4.4.0-beta)
        if (obj["__name"]) {
            return obj["__name"];
        }

        const constructor = obj.constructor;
        if (constructor && constructor.name && constructor.name !== "Object") {
            let name = constructor.name as string;
            while (name.startsWith("_")) name = name.substring(1);
            // remove any trailing numbers
            name = name.replace(/[0-9]+$/, "");
            // minified names are usually 1-3 characters long, so we skip those
            if (name.length > 3) {
                return name;
            }
        }

        return null;
    }

    function postprocessComponentProperty(property: IProperty) {
        switch (property.key) {
            case "sourceId":
            case "guid":
            case "needle_editor_guid":
                property.metadata.editable = false;
                break;
        }
    }
}




